import React, {useEffect, useMemo, useState} from 'react';
import {isTablet} from 'react-device-detect';
import {ProductBackgroundImage} from 'components/customer/Product/ProductStyled';
import styled from 'styled-components';
import {VariationRequest} from 'components/customer/JobDashboard/columns/VariationRequest';
import {InfoBox} from 'shared/components/InfoBox';
import {Product} from 'components/customer/Product/entity/Product';
import {useResizeObserver} from 'shared/helpers/useResizeObserver';
import {Stack} from 'react-bootstrap';
import {MobileDimensionEditor} from 'components/customer/JobDashboard/columns/MobileDimensionEditor';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {SelectionsOverview} from 'components/customer/JobDashboard/columns/SelectionsOverview';

interface ProductDetailProps {
    product: Product;
}

export const ProductDetail = ({product}: ProductDetailProps) => {
    const isSmallDevice = isDeviceSmall();
    const [cacheBust, setCacheBust] = useState(Date.now());
    const [showForm, setShowForm] = useState(false);

    const backgroundImage = useMemo(() => {
        if (product) {
            if (
                product.extMaterial &&
                product.extMaterial?.image != '' &&
                !product.type_image?.endsWith('.svg')
            ) {
                return `url("/uploads/gocabinet_materials/${product.extMaterial.image}`;
            }
        }

        return 'url("/uploads/gocabinet_materials/default_exterior.jpg)';
    }, [product]);

    useEffect(() => {
        setCacheBust(Date.now());
    }, [product]);

    const is3DImageLoading = Boolean(product.is_cabinet_3d_image_loading);

    const [horizontal, setHorizontal] = useState(false);
    const container = useResizeObserver((entries: ResizeObserverEntry[]) => {
        if (entries.length > 0) {
            const {contentRect} = entries[0];
            const {width} = contentRect;

            // NOTE: 266px out of 465px is covered by color and hardware icons
            // which is pretty much static. so we give 199px for variation
            // request. if there is enough space to fit 199px wide variation
            // request box horizontally we set this to true
            setHorizontal(width > 465);
        }
    });

    return (
        <div>
            <Stack>
                <Container $isMobile={isSmallDevice}>
                    <BorderImage $isMobile={isSmallDevice}>
                        <ProductBackgroundImage
                            style={{borderRadius: '10px'}}
                            backgroundImage={backgroundImage}
                            transform={product.ext_hor_grain}
                            src={`${product.type_image}?cache_bust=${cacheBust}`}
                            fallbackSrc={
                                !product.type_image?.startsWith('uploads')
                                    ? `/uploads/${product.type_image}`
                                    : ''
                            }
                            alt={product?.type_name}
                            cachingInProgress={is3DImageLoading}
                            placeholderImg={`url("/${product?.type_image_placeholder}"), ${backgroundImage}`}
                        />
                    </BorderImage>
                    <NameContainer
                        $horizontal={horizontal}
                        ref={container}
                        $isMobile={isSmallDevice}>
                        <Title
                            $isMobile={isSmallDevice}
                            $horizontal={horizontal}>
                            {!product.note ? product.type_name : product.note}
                        </Title>
                        <SelectionsOverview product={product} />
                        <VariationRequestOverride
                            product={{
                                ...product,
                                id: Number(product.id),
                            }}
                            $horizontal={horizontal}
                        />
                    </NameContainer>
                </Container>
                {isSmallDevice ? (
                    <Stack
                        direction="horizontal"
                        gap={2}
                        className={
                            showForm
                                ? 'justify-content-center'
                                : 'justify-content-between'
                        }>
                        {showForm ? null : (
                            <>
                                <NoWrapStrong>Dimensions</NoWrapStrong>
                                <div>{product.dimensions}</div>
                            </>
                        )}
                        <MobileDimensionEditor
                            row={product}
                            setShowForm={setShowForm}
                        />
                    </Stack>
                ) : null}
            </Stack>
        </div>
    );
};

const NoWrapStrong = styled.strong`
    color: ${({theme}) => theme.colors.primary?.main};
    white-space: nowrap;
`;

export const VariationRequestOverride = styled(VariationRequest)<{
    $horizontal?: boolean;
}>`
    ${({$horizontal}) => {
        if ($horizontal) {
            return `
                flex: 1;
                flex-direction: row;
                flex-wrap: wrap;
            `;
        }
    }}
`;

interface MobileProps {
    $isMobile?: boolean;
    $isTablet?: boolean;
    $horizontal?: boolean;
}

export const MaterialAndInfoBox = styled.div<MobileProps>`
    max-width: min-content;
    ${({$isMobile}) => {
        if ($isMobile) {
            return `width: 100%`;
        }
    }}
`;

export const BorderImage = styled.div<MobileProps>`
    width: ${({$isMobile}) =>
        $isMobile ? '75px' : isTablet ? '120px' : '150px'};
    height: ${({$isMobile}) =>
        $isMobile ? '75px' : isTablet ? '120px' : '150px'};
    outline-width: 1px;
    outline-style: solid;
    outline-color: ${({theme}) => theme.colors.secondary?.main};
    border-radius: 10px;

    .image-container {
        border-color: white !important;
    }
`;

export const Container = styled.div<MobileProps>`
    display: flex;
    gap: ${({$isMobile}) => ($isMobile || isTablet ? '5px' : '15px')};

    ${({$isMobile}) => {
        if (!$isMobile) {
            return `
                align-items:top;
            `;
        } else {
            return `
                position: relative;
                align-items: flex-start;
                padding-top: 3.5em;
            `;
        }
    }}
`;

export const Title = styled.strong<MobileProps>`
    font-size: 1.1em;
    color: ${({theme}) => theme.colors.secondary?.main} !important;
    text-align: left;
    flex-basis: ${({$horizontal}) => ($horizontal ? '100%' : 'initial')};

    ${({$isMobile}) => {
        if ($isMobile) {
            return `
                position: absolute;
                left: 0;
                right: 0;
                top: 0;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                word-break: break-word;
                overflow-wrap: break-word;
            `;
        } else {
            return `
                width: 30ch;
                min-width: 30ch;
                word-break: break-word;
                overflow-wrap: break-word;
                white-space: normal;
            `;
        }
    }}
`;

export const NameContainer = styled.div<{
    $horizontal?: boolean;
    $verticalGap?: number;
    $isMobile?: boolean;
}>`
    display: flex;
    flex-direction: ${({$horizontal}) => ($horizontal ? 'row' : 'column')};
    align-items: flex-start;
    flex-grow: 1;
    flex-basis: 100%;
    gap: ${({$horizontal, $verticalGap = 0}) =>
        $horizontal ? '10px' : `${$verticalGap}px`};
    flex-wrap: wrap;
`;

export const MaterialContainer = styled.div<MobileProps>`
    display: flex;
    flex-direction: row;

    ${({$isMobile}) => {
        if ($isMobile) {
            return `
                gap: 5px;
                padding: 0 5px 10px;
            `;
        } else {
            return `
                gap: 15px;
                padding: 0 15px 15px;

                > div {
                    min-width: 50px;
                }
            `;
        }
    }}
`;

export const Info = styled.section<MobileProps>`
    display: flex;
    flex-direction: row;
    gap: ${({$isMobile}) => ($isMobile ? '5px' : '10px')};
    width: max-content;
`;

export const NoShrinkInfoBox = styled(InfoBox)<MobileProps>`
    flex-shrink: 0;

    ${() => {
        if (isTablet) {
            return `
                flex-shrink: initial;
                font-size: 0.85em;
            `;
        }
    }}

    ${({$isMobile}) => {
        if ($isMobile) {
            return `
                width: 100%;
                font-size: 0.85em;
            `;
        }
    }}
`;

export const Detail = styled.div`
    margin-top: 5px;
    text-align: left;
    font-weight: 400;
    color: #363333;
`;
